/**
 * FundingChecker: "Can government funding pay for my ERP project?"
 * A stepped screener for Hong Kong companies on the NetSuite pricing page.
 * Asks the decision questions from funding-checker.slim.json one at a time,
 * then evaluates the routing table (base rows first, then modifiers in
 * priority order, removals always winning) and shows a plain verdict plus a
 * card per scheme that survives. The honest answer is usually "no", and the
 * copy says so. Every visible string lives in STR so it can be translated.
 * Requires React 16.8+. Injects scoped styles once.
 */
'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import rawData from '@/components/netsuite-pricing/data/funding-checker.slim.json';

const PSGLOBAL_LOGO =
  'https://images.prismic.io/onepac/ag7UFaYofJOwHelQ_psglobal_color.png?auto=format,compress';

/* -------------------------------- Types -------------------------------- */
type Status = 'open' | 'closed' | 'announced-not-open';
type Verdict = 'yes' | 'narrow' | 'no' | 'financing_only';
type Answers = Record<string, string>;
type Cond = Record<string, string | string[] | boolean>;

interface Option { value: string; label_en: string; applies_to_sector?: string[] }
interface Field { id: string; text_en: string; options: Option[] }
interface Question {
  id: string; order: number; text_en: string; type: 'single_choice' | 'compound';
  ask_when?: Record<string, string[]>; options?: Option[]; fields?: Field[];
}
interface Funding {
  type: string; government_share_pct?: number | string; government_share_note?: string;
  guarantee_coverage_pct?: number; per_project_cap_hkd?: number | string; per_project_cap_note?: string;
  cumulative_cap_hkd?: number | string; cumulative_cap_note?: string; cash_grant_cap_hkd?: number;
}
interface Scheme {
  id: string; name_en: string; short_name_en: string; name_zh: string; short_name_zh: string;
  status: Status; status_as_of: string; erp_verdict: Verdict; erp_verdict_reason: string;
  needs_secretariat_confirmation: boolean; funding: Funding;
  timing_rule: { verbatim?: string; verbatim_translation_note?: string; plain?: string; note?: string };
  urls: { official_en?: string; official_zh?: string; closure_notice_en?: string; recommended_link_en?: string };
}
interface Row {
  row: string; type: 'base' | 'modifier'; priority?: number; when: Cond;
  returns?: string[]; remove?: string[]; add?: string[]; keep_if_present?: string[];
  show_as_closed?: string[]; mention_as_not_for_systems?: string[]; footnote?: string[];
  annotate?: Record<string, Record<string, unknown>>; message_en: string; note?: string;
}
interface HardStopItem { scheme_ids?: string[]; scheme_id?: string; test?: string; closed?: string }
interface HardStop {
  id: string; rule_en: string; applies_to?: string[]; exception_en?: string;
  thresholds?: { scheme_ids: string[]; earliest_start: string }[]; items?: HardStopItem[];
}
interface Watch { id: string; scheme_ids: string[]; what: string; date?: string; expected?: string }
interface FundingData {
  meta: { verified_on: string; caveats: string[] };
  schemes: Scheme[]; decision_questions: Question[]; routing_table: Row[];
  hard_stops: HardStop[]; watchlist: Watch[];
}

const DATA = rawData as unknown as FundingData;
const SCHEMES: Record<string, Scheme> = Object.fromEntries(DATA.schemes.map((s) => [s.id, s]));

/* ------------------------------- Strings ------------------------------- */
const STR = {
  title: 'Can government funding pay for your ERP project?',
  sub: 'A few questions, then a straight answer. For most Hong Kong companies the answer is no, and this checker says so.',
  logoAlt: 'PS Global',
  progress: (n: number, total: number) => `Question ${n} of ${total}`,
  back: 'Back',
  startOver: 'Start over',
  resultHeading: 'Your result',
  verdictNoGrant: 'No live scheme funds a general-purpose ERP for your company.',
  verdictLoanOnly: 'A loan guarantee could finance it, but that is borrowing, not a subsidy.',
  verdictOneNames: 'One scheme names ERP as fundable for your company.',
  verdictOneNarrow: 'One scheme may apply, only in part and case by case. It is not an ERP grant.',
  verdictManyNarrow: (n: number) => `${n} schemes may apply, each only in part and case by case. None is an ERP grant.`,
  verdictMixed: (n: number) => `${n} routes may apply. One names ERP; the rest cover only part of a project.`,
  whyHeading: 'What shaped this answer',
  schemesHeading: 'Schemes that survive your answers',
  statusOpen: 'Open',
  statusClosed: 'Closed',
  statusAnnounced: 'Announced, not open',
  rowFunding: 'Funding',
  rowCovers: 'For an ERP',
  rowTiming: 'Timing',
  rowWatch: 'Watch',
  fundingUnpublished: 'Share and cap not published yet.',
  fundingLoan: (pct: number, cap: string) => `Guarantees ${pct}% of a bank loan; facility up to ${cap}. A loan, not a grant.`,
  fundingIncubation: (cap: string) => `Up to ${cap} for admitted incubatees. Not an ERP grant.`,
  govPays: (share: string) => `Government pays ${share}`,
  perProject: (cap: string) => `up to ${cap} per project`,
  perCompany: (cap: string) => `${cap} per company`,
  shareThird: 'one-third',
  shareTwoThirds: 'two-thirds',
  earliestStart: (when: string) => `Earliest start: ${when}.`,
  timingUnknown: 'No commencement rule published. Buy nothing until the rules are read.',
  verbatimLabel: 'Show the verbatim rule',
  confirmSecretariat: 'Confirm with the scheme secretariat before you sign.',
  officialPage: 'Official page',
  notForSystems: (names: string) => `${names}: not for systems. They fund marketing-type measures, not software.`,
  closedHeading: 'Closed schemes you may have read about',
  closedOn: (date: string) => `Closed ${date}`,
  closureNotice: 'Closure notice',
  voidsHeading: 'What voids eligibility',
  appliesTo: (names: string) => `Applies to ${names}.`,
  lastChecked: (date: string) => `Last checked ${date}.`,
  source: 'Scheme terms from the official pages, checked 2 October 2026. Terms change; confirm with the scheme secretariat.',
  months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};

/* -------------------------------- Rules -------------------------------- */
/**
 * Routing row B8's note: "Drop hkstp_incubation when years_operating_hk is
 * 5_to_7 or 7_plus; drop cyberport_incubation when 7_plus." The note is prose
 * in the data, so the gate is spelled out here.
 */
const INCUBATION_AGE_GATES: Record<string, string[]> = {
  hkstp_incubation: ['5_to_7', '7_plus'],
  cyberport_incubation: ['7_plus'],
};
/** Always listed under "Closed schemes you may have read about". */
const ALWAYS_CLOSED = ['tvp', 'dbiz', 'sfgs_100', 'sfgs_90'];
/** Hard stops shown in the "What voids eligibility" panel, in this order. */
const VOID_PANEL = ['started_too_early', 'listed_company', 'double_funding', 'general_purpose_software'];

export interface Step { key: string; text: string; options: Option[] }

/** Questions to ask, in order, given the answers so far. Compound questions become one step per field. */
export function buildSteps(answers: Answers): Step[] {
  const steps: Step[] = [];
  const qs = [...DATA.decision_questions].sort((a, b) => a.order - b.order);
  for (const q of qs) {
    if (q.ask_when && !Object.entries(q.ask_when).every(([k, vals]) => vals.includes(answers[k]))) continue;
    if (q.type === 'compound' && q.fields) {
      for (const f of q.fields) steps.push({ key: f.id, text: f.text_en, options: f.options });
      continue;
    }
    const options = (q.options ?? []).filter((o) => !o.applies_to_sector || o.applies_to_sector.includes(answers.sector));
    steps.push({ key: q.id, text: q.text_en, options });
  }
  return steps;
}

function matches(when: Cond, answers: Answers, resultEmpty: boolean): boolean {
  return Object.entries(when).every(([k, v]) => {
    if (k === 'always') return v === true;
    if (k === 'result_empty') return v === resultEmpty;
    const a = answers[k];
    if (a === undefined) return false;
    return Array.isArray(v) ? v.includes(a) : v === a;
  });
}

export interface Evaluation {
  schemes: string[]; closed: string[]; notForSystems: string[]; footnotes: string[];
  annotations: Record<string, Record<string, unknown>>; base?: Row; notes: Row[]; emptyRow?: Row;
}

/** Base rows first, then modifiers by priority. A removal by any matching row always wins over an add. */
export function evaluate(answers: Answers): Evaluation {
  const ordered: string[] = [];
  const removed = new Set<string>();
  const closed: string[] = [];
  const notForSystems: string[] = [];
  const footnotes: string[] = [];
  const annotations: Record<string, Record<string, unknown>> = {};
  const notes: Row[] = [];
  let emptyRow: Row | undefined;
  const push = (id: string) => { if (!ordered.includes(id)) ordered.push(id); };
  const pushAll = (list: string[], ids?: string[]) => { for (const id of ids ?? []) if (!list.includes(id)) list.push(id); };

  const base = DATA.routing_table.filter((r) => r.type === 'base').find((r) => matches(r.when, answers, false));
  if (base) {
    base.returns?.forEach(push);
    pushAll(closed, base.show_as_closed);
  }
  for (const [id, gate] of Object.entries(INCUBATION_AGE_GATES)) {
    if (gate.includes(answers.years_operating_hk)) removed.add(id);
  }

  const mods = DATA.routing_table.filter((r) => r.type === 'modifier').sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  for (const m of mods) {
    const current = ordered.filter((id) => !removed.has(id));
    if (!matches(m.when, answers, current.length === 0)) continue;
    m.remove?.forEach((id) => removed.add(id));
    m.add?.forEach(push);
    pushAll(closed, m.show_as_closed);
    pushAll(notForSystems, m.mention_as_not_for_systems);
    pushAll(footnotes, m.footnote);
    if (m.annotate) {
      for (const [id, patch] of Object.entries(m.annotate)) annotations[id] = { ...(annotations[id] ?? {}), ...patch };
    }
    if (m.when.result_empty === true) emptyRow = m;
    else if (m.when.always !== true) notes.push(m);
  }

  return { schemes: ordered.filter((id) => !removed.has(id)), closed, notForSystems, footnotes, annotations, base, notes, emptyRow };
}

/* ------------------------------ Formatting ----------------------------- */
function hkd(n: number): string {
  if (n >= 1_000_000) return `HK$${String(parseFloat((n / 1_000_000).toFixed(2)))}m`;
  return `HK$${String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}
function shareLabel(pct: number): string {
  if (Math.abs(pct - 33.33) < 0.1) return STR.shareThird;
  if (Math.abs(pct - 66.67) < 0.1) return STR.shareTwoThirds;
  return `${String(pct)}%`;
}
/** "2024-12-31" -> "31 December 2024"; "2025-05 (first round)" -> "May 2025 (first round)". */
function formatDate(raw: string): string {
  const m = raw.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?(.*)$/);
  if (!m) return raw;
  const month = STR.months[parseInt(m[2], 10) - 1] ?? m[2];
  const rest = m[4].replace(/^\s*\d{2}:\d{2}\s*HKT/, '').trim();
  const core = m[3] ? `${parseInt(m[3], 10)} ${month} ${m[1]}` : `${month} ${m[1]}`;
  return rest ? `${core} ${rest}` : core;
}
function fundingLine(s: Scheme, ann?: Record<string, unknown>): string {
  const f = s.funding;
  if (f.type.startsWith('loan guarantee')) {
    return STR.fundingLoan(f.guarantee_coverage_pct ?? 0, typeof f.cumulative_cap_hkd === 'number' ? hkd(f.cumulative_cap_hkd) : '');
  }
  if (f.type.startsWith('incubation')) {
    const cap = f.cash_grant_cap_hkd ?? (typeof f.cumulative_cap_hkd === 'number' ? f.cumulative_cap_hkd : 0);
    return STR.fundingIncubation(hkd(cap));
  }
  const annotated = ann?.government_share_pct;
  const pct = typeof annotated === 'number' ? annotated : f.government_share_pct;
  if (typeof pct !== 'number') return STR.fundingUnpublished;
  const parts = [STR.govPays(shareLabel(pct))];
  if (typeof f.per_project_cap_hkd === 'number') {
    // Short cap notes ("HK$100,000 in total per warehouse premises") are more accurate than "per project".
    parts.push(f.per_project_cap_note && f.per_project_cap_note.length <= 120 ? f.per_project_cap_note : STR.perProject(hkd(f.per_project_cap_hkd)));
  }
  if (typeof f.cumulative_cap_hkd === 'number' && f.cumulative_cap_hkd !== f.per_project_cap_hkd) {
    parts.push(STR.perCompany(hkd(f.cumulative_cap_hkd)));
  }
  return `${parts.join(', ')}.`;
}
function timingLine(s: Scheme): string {
  const stop = DATA.hard_stops.find((h) => h.id === 'started_too_early');
  const t = stop?.thresholds?.find((th) => th.scheme_ids.includes(s.id));
  if (t && !/^no /i.test(t.earliest_start)) return STR.earliestStart(t.earliest_start);
  // timing_rule.note is research commentary, not visitor copy, so it is not shown.
  return s.timing_rule.plain ?? STR.timingUnknown;
}
function schemeLink(s: Scheme): string | undefined {
  if (s.urls.recommended_link_en) return s.urls.recommended_link_en;
  if (s.status === 'closed') return s.urls.closure_notice_en ?? s.urls.official_en;
  return s.urls.official_en;
}
function closedDate(id: string): string | undefined {
  const stop = DATA.hard_stops.find((h) => h.id === 'closed_scheme');
  const item = stop?.items?.find((i) => i.scheme_id === id);
  return item?.closed ? formatDate(item.closed) : undefined;
}
function watchFor(id: string): Watch | undefined {
  return DATA.watchlist.find((w) => w.scheme_ids.includes(id));
}
function shortNames(ids: string[], openOnly = false): string {
  return ids
    .map((id) => SCHEMES[id])
    .filter((s): s is Scheme => !!s && (!openOnly || s.status !== 'closed'))
    .map((s) => s.short_name_en)
    .join(', ');
}
const STATUS_LABEL: Record<Status, string> = {
  open: STR.statusOpen, closed: STR.statusClosed, 'announced-not-open': STR.statusAnnounced,
};

/* --------------------------- Scoped styles ----------------------------- */
const STYLE_ID = 'fc-styles';
const CSS = `
.fc-root { --fc-font: 'Poppins', system-ui, -apple-system, sans-serif;
  --ink: #1B2A33; --ink-2: #3E5460; --body: #5B6B75; --muted: #8A99A3;
  --line: #E4E7EA; --paper: #FFFFFF; --wash: #F5F8F8;
  --teal: #0f9993; --teal-deep: #0d555c; --teal-900: #04445c; --gold: #B08A2E; --red: #D92B03;
  position: relative; box-sizing: border-box; width: 100%; max-width: 860px;
  background: var(--paper); border: 1px solid var(--line); border-radius: 20px;
  box-shadow: 0 10px 20px -10px rgba(4,68,92,0.35);
  padding: 40px 44px 28px; color: var(--ink);
  font-family: var(--fc-font); -webkit-font-smoothing: antialiased; }
.fc-root *, .fc-root *::before, .fc-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
.fc-root h2, .fc-root h3, .fc-root h4, .fc-root p, .fc-root ul, .fc-root dl, .fc-root img { margin: 0; }
.fc-root ul { list-style: none; }
.fc-root p { letter-spacing: normal; }
.fc-root a { color: var(--teal-deep); text-decoration: underline; text-underline-offset: 2px; }
.fc-root a:hover { color: var(--teal); }
.fc-root button { font: inherit; color: inherit; background: none; border: 0; cursor: pointer; }
.fc-root :focus-visible { outline: 3px solid rgba(15,153,147,0.55); outline-offset: 2px; border-radius: 8px; }
.fc-root .fc-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
.fc-root .fc-logo { height: 40px; width: auto; flex: none; }
.fc-root h2 { font-size: 27px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.3; max-width: 560px; }
.fc-root .fc-sub { font-size: 14px; color: var(--body); margin-top: 10px; max-width: 620px; line-height: 1.55; }
.fc-root .fc-step { margin-top: 28px; }
.fc-root .fc-progress { display: flex; align-items: center; justify-content: space-between; gap: 16px; font-size: 12.5px; color: var(--muted); }
.fc-root .fc-bar { flex: 1; height: 4px; background: var(--wash); border-radius: 2px; overflow: hidden; }
.fc-root .fc-bar span { display: block; height: 100%; background: var(--teal); border-radius: 2px; transition: width .35s ease; }
.fc-root .fc-q { margin-top: 18px; font-size: 20px; font-weight: 600; line-height: 1.35; color: var(--ink); }
.fc-root .fc-q:focus { outline: none; }
.fc-root .fc-opts { margin-top: 16px; display: grid; gap: 10px; }
.fc-root .fc-opt { display: flex; align-items: center; gap: 14px; width: 100%; min-height: 56px; padding: 14px 18px; text-align: left;
  font-size: 15px; line-height: 1.4; color: var(--ink-2); background: var(--paper); border: 1.5px solid var(--line); border-radius: 12px;
  transition: border-color .15s, background .15s, transform .15s; }
.fc-root .fc-opt:hover { border-color: var(--teal); background: var(--wash); }
.fc-root .fc-opt:active { transform: scale(0.995); }
.fc-root .fc-opt[aria-pressed="true"] { border-color: var(--teal); background: var(--wash); color: var(--ink); }
.fc-root .fc-opt .fc-dot { flex: none; width: 18px; height: 18px; border: 1.5px solid var(--muted); border-radius: 50%; position: relative; }
.fc-root .fc-opt[aria-pressed="true"] .fc-dot { border-color: var(--teal); }
.fc-root .fc-opt[aria-pressed="true"] .fc-dot::after { content: ''; position: absolute; inset: 3px; border-radius: 50%; background: var(--teal); }
.fc-root .fc-nav { margin-top: 18px; display: flex; gap: 12px; flex-wrap: wrap; }
.fc-root .fc-btn { min-height: 44px; padding: 10px 18px; border-radius: 10px; font-size: 14px; font-weight: 500; color: var(--teal-deep);
  border: 1.5px solid var(--line); background: var(--paper); }
.fc-root .fc-btn:hover { border-color: var(--teal); background: var(--wash); }
.fc-root .fc-result { margin-top: 28px; }
.fc-root .fc-kicker { font-size: 12px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: var(--gold); }
.fc-root .fc-verdict { margin-top: 8px; font-size: 22px; font-weight: 600; line-height: 1.35; color: var(--ink); }
.fc-root .fc-verdict.fc-no { color: var(--red); }
.fc-root .fc-lead { margin-top: 12px; font-size: 15px; line-height: 1.6; color: var(--ink-2); }
.fc-root .fc-caveat { margin-top: 10px; font-size: 12.5px; line-height: 1.55; color: var(--muted); }
.fc-root .fc-h4 { margin-top: 26px; font-size: 13px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--teal-900); }
.fc-root .fc-notes { margin-top: 10px; display: grid; gap: 8px; }
.fc-root .fc-notes li { position: relative; padding-left: 18px; font-size: 14px; line-height: 1.55; color: var(--body); }
.fc-root .fc-notes li::before { content: ''; position: absolute; left: 0; top: 9px; width: 8px; height: 8px; border-radius: 50%; background: var(--teal); opacity: .6; }
.fc-root .fc-cards { margin-top: 12px; display: grid; gap: 14px; }
.fc-root .fc-card { border: 1px solid var(--line); border-radius: 14px; padding: 18px 20px; background: var(--paper); }
.fc-root .fc-card-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; }
.fc-root .fc-name { font-size: 16px; font-weight: 600; line-height: 1.35; color: var(--ink); }
.fc-root .fc-name-zh { margin-top: 2px; font-size: 13.5px; color: var(--body); line-height: 1.5; }
.fc-root .fc-badge { flex: none; font-size: 11.5px; font-weight: 600; letter-spacing: .04em; padding: 4px 10px; border-radius: 999px; white-space: nowrap; }
.fc-root .fc-badge.open { color: #fff; background: var(--teal); }
.fc-root .fc-badge.closed { color: var(--ink-2); background: var(--line); }
.fc-root .fc-badge.announced-not-open { color: #fff; background: var(--gold); }
.fc-root .fc-rows { margin-top: 14px; display: grid; gap: 9px; }
.fc-root .fc-row { display: grid; grid-template-columns: 96px 1fr; gap: 12px; font-size: 14px; line-height: 1.55; }
.fc-root .fc-row dt { color: var(--muted); font-size: 12.5px; font-weight: 500; padding-top: 2px; }
.fc-root .fc-row dd { color: var(--ink-2); }
.fc-root .fc-when { display: block; margin-top: 2px; font-size: 12.5px; font-weight: 500; color: var(--teal-deep); }
.fc-root .fc-flag { margin-top: 12px; font-size: 13.5px; font-weight: 500; color: var(--gold); line-height: 1.5; }
.fc-root .fc-flag::before { content: '!'; display: inline-block; width: 16px; height: 16px; margin-right: 8px; border-radius: 50%; background: var(--gold); color: #fff; font-size: 11px; font-weight: 700; text-align: center; line-height: 16px; }
.fc-root .fc-card-foot { margin-top: 12px; font-size: 13.5px; }
.fc-root .fc-tip-wrap { position: relative; display: inline-block; margin-left: 6px; vertical-align: middle; }
.fc-root .fc-tip-btn { width: 22px; height: 22px; border-radius: 50%; border: 1.5px solid var(--line); color: var(--teal-deep); font-size: 12px; font-weight: 600; line-height: 1; }
.fc-root .fc-tip-btn:hover { border-color: var(--teal); background: var(--wash); }
.fc-root .fc-tip { position: absolute; left: 0; bottom: calc(100% + 10px); z-index: 5; width: min(360px, 78vw); padding: 10px 13px; border-radius: 10px;
  background: var(--ink); color: #fff; font-size: 12.5px; line-height: 1.55; font-style: italic; box-shadow: 0 12px 30px -12px rgba(0,0,0,0.5);
  opacity: 0; visibility: hidden; transition: opacity .15s; pointer-events: none; }
.fc-root .fc-tip .fc-tip-note { display: block; margin-top: 6px; font-style: normal; color: #A9D9D4; font-size: 11.5px; }
.fc-root .fc-tip-wrap:hover .fc-tip, .fc-root .fc-tip-wrap:focus-within .fc-tip, .fc-root .fc-tip-wrap.open .fc-tip { opacity: 1; visibility: visible; }
.fc-root .fc-closed { margin-top: 24px; border: 1px solid var(--line); border-radius: 12px; background: var(--wash); }
.fc-root .fc-closed summary { cursor: pointer; padding: 12px 16px; font-size: 14px; font-weight: 500; color: var(--ink-2); list-style: none; display: flex; align-items: center; gap: 10px; }
.fc-root .fc-closed summary::-webkit-details-marker { display: none; }
.fc-root .fc-closed summary::before { content: ''; width: 7px; height: 7px; border-right: 1.5px solid var(--muted); border-bottom: 1.5px solid var(--muted); transform: rotate(-45deg); transition: transform .15s; flex: none; }
.fc-root .fc-closed[open] summary::before { transform: rotate(45deg); }
.fc-root .fc-closed ul { padding: 0 16px 14px; display: grid; gap: 10px; }
.fc-root .fc-closed li { font-size: 13.5px; line-height: 1.5; color: var(--body); }
.fc-root .fc-closed li b { font-weight: 600; color: var(--ink-2); }
.fc-root .fc-closed li .zh { color: var(--muted); }
.fc-root .fc-void { margin-top: 24px; border-left: 3px solid var(--red); padding: 2px 0 2px 16px; }
.fc-root .fc-void .fc-h4 { margin-top: 0; color: var(--red); }
.fc-root .fc-void ul { margin-top: 10px; display: grid; gap: 10px; }
.fc-root .fc-void li { font-size: 13.5px; line-height: 1.55; color: var(--body); }
.fc-root .fc-void li b { font-weight: 600; color: var(--ink-2); }
.fc-root .fc-void .fc-th { margin-top: 6px; display: grid; gap: 3px; font-size: 12.5px; color: var(--muted); }
.fc-root .fc-void .fc-th span b { color: var(--body); font-weight: 500; }
.fc-root .fc-checked { margin-top: 18px; font-size: 12.5px; color: var(--muted); }
.fc-root .fc-foot { margin-top: 24px; padding-top: 16px; border-top: 1px solid var(--line); }
.fc-root .fc-src { font-size: 10px; color: var(--muted); line-height: 1.6; }
.fc-root.compact { padding: 26px 0 20px; border-radius: 0; border-left: none; border-right: none; box-shadow: none; }
.fc-root.compact .fc-logo { display: none; }
.fc-root.compact h2 { font-size: 22px; }
.fc-root.narrow { padding-left: 18px; padding-right: 18px; }
.fc-root.narrow h2 { font-size: 22px; }
.fc-root.narrow .fc-q { font-size: 18px; }
.fc-root.narrow .fc-opt { padding: 12px 14px; font-size: 14px; }
.fc-root.narrow .fc-verdict { font-size: 19px; }
.fc-root.narrow .fc-card { padding: 14px; }
.fc-root.narrow .fc-card-head { flex-direction: column; }
.fc-root.narrow .fc-row { grid-template-columns: 1fr; gap: 2px; }
.fc-root.compact.narrow { padding-left: 0; padding-right: 0; }`;

function useInjectedStyles(): void {
  useEffect(() => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent !== CSS) existing.textContent = CSS; // refresh stale CSS after hot-reload
    } else {
      const s = document.createElement('style');
      s.id = STYLE_ID; s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

/* ------------------------------ Sub-views ------------------------------ */
const VerbatimTip: React.FC<{ id: string; text: string; note?: string }> = ({ id, text, note }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className={`fc-tip-wrap${open ? ' open' : ''}`}>
      <button type="button" className="fc-tip-btn" aria-label={STR.verbatimLabel} aria-describedby={id}
        onClick={() => setOpen((o) => !o)} onBlur={() => setOpen(false)}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}>
        &ldquo;
      </button>
      <span role="tooltip" id={id} className="fc-tip">
        &ldquo;{text}&rdquo;
        {note && <span className="fc-tip-note">{note}</span>}
      </span>
    </span>
  );
};

const SchemeCard: React.FC<{ s: Scheme; ann?: Record<string, unknown> }> = ({ s, ann }) => {
  const link = schemeLink(s);
  const watch = watchFor(s.id);
  const caveat = typeof ann?.caveat === 'string' ? ann.caveat : undefined;
  return (
    <li className="fc-card">
      <div className="fc-card-head">
        <div>
          <p className="fc-name">{s.name_en}</p>
          <p className="fc-name-zh" lang="zh-Hant">{s.name_zh}</p>
        </div>
        <span className={`fc-badge ${s.status}`}>{STATUS_LABEL[s.status]}</span>
      </div>
      <dl className="fc-rows">
        <div className="fc-row"><dt>{STR.rowFunding}</dt><dd>{fundingLine(s, ann)}</dd></div>
        <div className="fc-row"><dt>{STR.rowCovers}</dt><dd>{s.erp_verdict_reason}</dd></div>
        <div className="fc-row">
          <dt>{STR.rowTiming}</dt>
          <dd>
            {timingLine(s)}
            {s.timing_rule.verbatim && <VerbatimTip id={`fc-tip-${s.id}`} text={s.timing_rule.verbatim} note={s.timing_rule.verbatim_translation_note} />}
          </dd>
        </div>
        {watch && (
          <div className="fc-row">
            <dt>{STR.rowWatch}</dt>
            <dd>
              {watch.what}
              {(watch.date || watch.expected) && <span className="fc-when">{watch.date ? formatDate(watch.date) : watch.expected}</span>}
            </dd>
          </div>
        )}
      </dl>
      {caveat && <p className="fc-caveat">{caveat}</p>}
      {s.needs_secretariat_confirmation && <p className="fc-flag">{STR.confirmSecretariat}</p>}
      {link && (
        <p className="fc-card-foot">
          <a href={link} target="_blank" rel="noopener noreferrer">{STR.officialPage}</a>
        </p>
      )}
    </li>
  );
};

const ClosedList: React.FC<{ ids: string[] }> = ({ ids }) => (
  <details className="fc-closed">
    <summary>{STR.closedHeading}</summary>
    <ul>
      {ids.map((id) => {
        const s = SCHEMES[id];
        if (!s) return null;
        const date = closedDate(id);
        const link = s.urls.closure_notice_en ?? s.urls.recommended_link_en ?? s.urls.official_en;
        return (
          <li key={id}>
            <b>{s.name_en}</b> <span className="zh" lang="zh-Hant">{s.name_zh}</span>
            {date && <>. {STR.closedOn(date)}</>}
            {link && <>. <a href={link} target="_blank" rel="noopener noreferrer">{STR.closureNotice}</a></>}
          </li>
        );
      })}
    </ul>
  </details>
);

const VoidPanel: React.FC = () => (
  <section className="fc-void" aria-labelledby="fc-void-h">
    <h4 className="fc-h4" id="fc-void-h">{STR.voidsHeading}</h4>
    <ul>
      {VOID_PANEL.map((id) => {
        const h = DATA.hard_stops.find((x) => x.id === id);
        if (!h) return null;
        return (
          <li key={id}>
            <b>{h.rule_en}</b>
            {h.thresholds && (
              <span className="fc-th">
                {h.thresholds.filter((t) => !/^no /i.test(t.earliest_start)).map((t) => (
                  <span key={t.scheme_ids.join('+')}><b>{shortNames(t.scheme_ids)}</b>: {t.earliest_start}</span>
                ))}
              </span>
            )}
            {h.exception_en && <span className="fc-th"><span>{h.exception_en}</span></span>}
            {!h.thresholds && h.applies_to && <span className="fc-th"><span>{STR.appliesTo(shortNames(h.applies_to, true))}</span></span>}
          </li>
        );
      })}
    </ul>
  </section>
);

/* ------------------------------ Component ------------------------------ */
export interface FundingCheckerProps { compact?: boolean }

const FundingChecker: React.FC<FundingCheckerProps> = ({ compact = false }) => {
  useInjectedStyles();
  const rootRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [narrow, setNarrow] = useState(false);
  const [answers, setAnswers] = useState<Answers>({});
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = (): void => setNarrow(el.offsetWidth < 640);
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, []);

  const steps = useMemo(() => buildSteps(answers), [answers]);
  const step = steps[Math.min(index, steps.length - 1)];
  const result = useMemo(() => (done ? evaluate(answers) : null), [done, answers]);

  useEffect(() => {
    if (index === 0 && !done) return; // do not steal focus on first paint
    headingRef.current?.focus({ preventScroll: true });
  }, [index, done]);

  const choose = (value: string): void => {
    const next: Answers = { ...answers, [step.key]: value };
    // Answers to questions that no longer apply (e.g. operations_location after a sector change) are dropped.
    const keys = new Set(buildSteps(next).map((s) => s.key));
    for (const k of Object.keys(next)) if (!keys.has(k)) delete next[k];
    setAnswers(next);
    const nextSteps = buildSteps(next);
    const pos = nextSteps.findIndex((s) => s.key === step.key);
    if (pos + 1 >= nextSteps.length) setDone(true);
    else setIndex(pos + 1);
  };
  const back = (): void => {
    if (done) { setDone(false); setIndex(steps.length - 1); return; }
    setIndex((i) => Math.max(0, i - 1));
  };
  const restart = (): void => { setAnswers({}); setIndex(0); setDone(false); };

  const cls = `fc-root${compact ? ' compact' : ''}${narrow ? ' narrow' : ''}`;

  return (
    <div className={cls} ref={rootRef}>
      <div className="fc-head">
        <h2>{STR.title}</h2>
        <img className="fc-logo" src={PSGLOBAL_LOGO} alt={STR.logoAlt} />
      </div>
      <p className="fc-sub">{STR.sub}</p>

      {!done && step && (
        <div className="fc-step">
          <div className="fc-progress">
            <span aria-live="polite">{STR.progress(index + 1, steps.length)}</span>
            <div className="fc-bar" aria-hidden="true"><span style={{ width: `${((index) / steps.length) * 100}%` }} /></div>
          </div>
          <h3 className="fc-q" ref={headingRef} tabIndex={-1}>{step.text}</h3>
          <div className="fc-opts" role="group" aria-label={step.text}>
            {step.options.map((o) => (
              <button type="button" key={o.value} className="fc-opt" aria-pressed={answers[step.key] === o.value} onClick={() => choose(o.value)}>
                <span className="fc-dot" aria-hidden="true" />
                <span>{o.label_en}</span>
              </button>
            ))}
          </div>
          {index > 0 && (
            <div className="fc-nav">
              <button type="button" className="fc-btn" onClick={back}>{STR.back}</button>
            </div>
          )}
        </div>
      )}

      {done && result && <ResultView result={result} headingRef={headingRef} onBack={back} onRestart={restart} />}

      <div className="fc-foot">
        <p className="fc-src">{STR.source}</p>
      </div>
    </div>
  );
};

const ResultView: React.FC<{
  result: Evaluation; headingRef: React.RefObject<HTMLHeadingElement | null>; onBack: () => void; onRestart: () => void;
}> = ({ result, headingRef, onBack, onRestart }) => {
  const returned = result.schemes.map((id) => SCHEMES[id]).filter((s): s is Scheme => !!s);
  const grants = returned.filter((s) => s.erp_verdict === 'yes' || s.erp_verdict === 'narrow');
  const named = grants.filter((s) => s.erp_verdict === 'yes').length;
  const financing = returned.some((s) => s.erp_verdict === 'financing_only');

  let verdict: string;
  let negative = false;
  if (returned.length === 0) { verdict = result.emptyRow?.message_en ?? STR.verdictNoGrant; negative = true; }
  else if (grants.length === 0) { verdict = `${STR.verdictNoGrant}${financing ? ` ${STR.verdictLoanOnly}` : ''}`; negative = true; }
  else if (named > 0 && grants.length === 1) verdict = STR.verdictOneNames;
  else if (named > 0) verdict = STR.verdictMixed(grants.length);
  else if (grants.length === 1) verdict = STR.verdictOneNarrow;
  else verdict = STR.verdictManyNarrow(grants.length);

  const closedIds = [...result.closed, ...ALWAYS_CLOSED].filter((id, i, arr) => arr.indexOf(id) === i);
  const footnoteSchemes = result.footnotes.map((id) => SCHEMES[id]).filter((s): s is Scheme => !!s);

  return (
    <div className="fc-result">
      <p className="fc-kicker">{STR.resultHeading}</p>
      <h3 className={`fc-verdict${negative ? ' fc-no' : ''}`} ref={headingRef} tabIndex={-1}>{verdict}</h3>
      {result.base && <p className="fc-lead">{result.base.message_en}</p>}
      <p className="fc-caveat">{DATA.meta.caveats[0]}</p>

      {result.notes.length > 0 && (
        <>
          <h4 className="fc-h4">{STR.whyHeading}</h4>
          <ul className="fc-notes">
            {result.notes.map((n) => <li key={n.row}>{n.message_en}</li>)}
            {result.notForSystems.length > 0 && <li>{STR.notForSystems(shortNames(result.notForSystems))}</li>}
            {footnoteSchemes.map((s) => <li key={s.id}>{s.short_name_en}: {s.erp_verdict_reason}</li>)}
          </ul>
        </>
      )}

      {returned.length > 0 && (
        <>
          <h4 className="fc-h4">{STR.schemesHeading}</h4>
          <ul className="fc-cards">
            {returned.map((s) => <SchemeCard key={s.id} s={s} ann={result.annotations[s.id]} />)}
          </ul>
        </>
      )}

      <p className="fc-caveat">{DATA.meta.caveats[1]}</p>

      <ClosedList ids={closedIds} />
      <VoidPanel />

      <p className="fc-checked">{STR.lastChecked(formatDate(DATA.meta.verified_on))}</p>
      <div className="fc-nav">
        <button type="button" className="fc-btn" onClick={onBack}>{STR.back}</button>
        <button type="button" className="fc-btn" onClick={onRestart}>{STR.startOver}</button>
      </div>
    </div>
  );
};

export default FundingChecker;
